// Members: a searchable list with status tabs, and a profile with tabs for earnings, cash-outs,
// ledger, network and activity. Every money or status action goes through a dialog with a reason.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Coins,
  Flag,
  Monitor,
  Network,
  ShieldCheck,
  Smartphone,
  UserCog,
  Users as UsersIcon,
} from 'lucide-react';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import { flagLabel, humanize } from '../lib/risk';
import { CoinGlyph } from '../components/Coin';
import {
  ID_CHECK_VALID_MS,
  IdChecks,
  VerificationPills,
  type IdCheck,
} from '../components/IdCheck';
import {
  actorLabel,
  askAmount,
  Card,
  confirmAction,
  DeletedMark,
  EmptyState,
  ErrorNote,
  fmtMoney,
  fmtNum,
  fmtUsd,
  Initial,
  KV,
  Mono,
  PageHeader,
  Pill,
  RiskMeter,
  run,
  ScrollX,
  SearchInput,
  SeverityPill,
  SkeletonRows,
  StatusPill,
  statusLabel,
  Tabs,
  TimeAgo,
  useDebounced,
  UserChip,
} from '../components/ui';

interface UserRow {
  id: string;
  /** null for a deleted account (its address was erased). */
  email: string | null;
  username: string;
  status: string;
  signupCountry: string | null;
  emailVerifiedAt: number | null;
  /** When the latest passed identity check was decided; null if none passed. */
  idApprovedAt: number | null;
  createdAt: number;
  riskScore: number;
  available: number;
  openFlags: number;
}

const LIST_TABS = [
  { key: 'all', label: 'Everyone' },
  { key: 'flagged', label: 'Open flags' },
  { key: 'review', label: 'In review' },
  { key: 'restricted', label: 'Restricted' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'deleted', label: 'Deleted' },
] as const;
type ListTab = (typeof LIST_TABS)[number]['key'];

/** Coins with the coin glyph so the unit is never in doubt; red with a minus when negative. */
function CoinAmount({ n, signed = false }: { n: number; signed?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap font-bold tabular-nums ${n < 0 ? 'text-berry-deep' : ''}`}
    >
      <CoinGlyph className="h-3.5 w-3.5" />
      {/* The number, not the glyph, gives the row its baseline. */}
      <span className="self-baseline">
        {n < 0 ? '−' : signed && n > 0 ? '+' : ''}
        {fmtNum(Math.abs(n))}
        <span className="sr-only"> coins</span>
      </span>
    </span>
  );
}

function UserStatusPills({ u }: { u: UserRow }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <StatusPill status={u.status} />
      {!u.emailVerifiedAt && u.status !== 'deleted' && <Pill tone="sun">Email unverified</Pill>}
      {u.idApprovedAt !== null && u.idApprovedAt > Date.now() - ID_CHECK_VALID_MS && (
        <Pill tone="mint">
          <ShieldCheck size={11} aria-hidden="true" /> ID verified
        </Pill>
      )}
      {u.openFlags > 0 && (
        <Pill tone="berry">
          <Flag size={11} aria-hidden="true" /> {u.openFlags}
          <span className="sr-only"> open flag{u.openFlags === 1 ? '' : 's'}</span>
        </Pill>
      )}
    </span>
  );
}

export function Users() {
  const [params, setParams] = useSearchParams();
  const tab = (LIST_TABS.find((t) => t.key === params.get('tab'))?.key ?? 'all') as ListTab;
  const [q, setQ] = useState('');
  const search = useDebounced(q.trim());
  const [page, setPage] = useState(0);
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['users', tab, search, page],
    queryFn: () =>
      api<{
        users: UserRow[];
        total: number;
        pageSize: number;
        counts: Record<string, number>;
      }>(
        `/users?page=${page}${tab !== 'all' ? `&status=${tab}` : ''}${search ? `&q=${encodeURIComponent(search)}` : ''}`,
      ),
    placeholderData: (p) => p,
  });
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  return (
    <div className="space-y-5">
      <PageHeader
        title="Users"
        description="Everyone who signed up. Open a profile to see earnings, cash-outs, the ledger and which other accounts share their network."
        actions={
          <SearchInput
            value={q}
            onChange={(v) => {
              setQ(v);
              setPage(0);
            }}
            placeholder="Username, email or usr_ id"
            className="w-full sm:w-72"
          />
        }
      />
      <Tabs
        label="Account status"
        value={tab}
        onChange={(k) => {
          setPage(0);
          setParams((p) => (k === 'all' ? p.delete('tab') : p.set('tab', k), p), { replace: true });
        }}
        items={LIST_TABS.map((t) => ({
          key: t.key,
          label: t.label,
          count: t.key === 'all' ? undefined : data?.counts[t.key],
          hot: t.key === 'flagged',
        }))}
      />
      <ErrorNote error={error} onRetry={() => void refetch()} />
      {error && !data ? null : (
        <div className="card overflow-hidden">
          {isLoading ? (
            <SkeletonRows />
          ) : !data || data.users.length === 0 ? (
            <EmptyState
              icon={UsersIcon}
              title="No users match"
              action={
                search ? (
                  <button className="btn-ghost px-4 py-2 text-[14px]" onClick={() => setQ('')}>
                    Clear search
                  </button>
                ) : undefined
              }
            >
              {search ? `Nobody matches “${search}” here.` : 'Nobody in this group.'}
            </EmptyState>
          ) : (
            <>
              {/* Phones: one row per person, everything that matters without scrolling sideways. */}
              <ul className="divide-y divide-hairline md:hidden">
                {data.users.map((u) => (
                  <li key={u.id} className="space-y-2.5 px-4 py-3.5">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <UserChip
                          id={u.id}
                          username={u.username}
                          sub={u.email}
                          deleted={u.status === 'deleted'}
                        />
                      </div>
                      <div className="shrink-0 text-right">
                        <CoinAmount n={u.available} />
                        <div className="text-[12px] tabular-nums text-stone">
                          {fmtUsd(u.available / 1000)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-3 pl-[42px]">
                      <div className="min-w-0 space-y-1.5">
                        <UserStatusPills u={u} />
                        <div className="text-[12px] text-stone">
                          {u.signupCountry ?? 'No country'} · Joined <TimeAgo t={u.createdAt} />
                        </div>
                      </div>
                      <span className="shrink-0 pt-0.5">
                        <RiskMeter score={u.riskScore || null} />
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              <ScrollX className="hidden md:block">
                <table className="data-table table-compact">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Status</th>
                      <th>Country</th>
                      <th className="text-right">Balance</th>
                      <th>Risk</th>
                      <th className="text-right">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((u) => (
                      <tr key={u.id}>
                        <td className="max-w-[260px]">
                          <UserChip
                            id={u.id}
                            username={u.username}
                            sub={u.email}
                            deleted={u.status === 'deleted'}
                          />
                        </td>
                        <td>
                          <UserStatusPills u={u} />
                        </td>
                        <td className="text-[13px]">{u.signupCountry ?? '—'}</td>
                        <td className="text-right">
                          <CoinAmount n={u.available} />
                          <div className="text-[12px] text-stone">{fmtUsd(u.available / 1000)}</div>
                        </td>
                        <td>
                          <RiskMeter score={u.riskScore || null} />
                        </td>
                        <td className="whitespace-nowrap text-right text-[13px] text-stone">
                          <TimeAgo t={u.createdAt} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollX>
            </>
          )}
          {data && pages > 1 && (
            <nav
              aria-label="Pages"
              className="flex items-center justify-between gap-3 border-t border-hairline px-4 py-2.5 text-[13px] text-stone sm:px-5"
            >
              <span className="tabular-nums">
                {fmtNum(page * data.pageSize + 1)}–
                {fmtNum(Math.min(data.total, (page + 1) * data.pageSize))} of {fmtNum(data.total)}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="mr-1 hidden tabular-nums sm:inline">
                  Page {page + 1} of {fmtNum(pages)}
                </span>
                <button
                  className="btn-ghost h-10 w-10 p-0 sm:h-9 sm:w-9"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  className="btn-ghost h-10 w-10 p-0 sm:h-9 sm:w-9"
                  disabled={page >= pages - 1}
                  onClick={() => setPage(page + 1)}
                  aria-label="Next page"
                >
                  <ChevronRight size={16} />
                </button>
              </span>
            </nav>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Profile */

interface UserDetailData {
  user: UserRow & {
    referredBy: string | null;
    statusReason: string | null;
    referralCode: string;
    countryCode: string | null;
    /** When the owner's personal data was erased (status "deleted"). */
    deletedAt: number | null;
    /** Since when no app events go to AppsFlyer (Global Privacy Control, or staff on request). */
    attributionOptOutAt?: number | null;
  };
  balance: { available: number; pending: number; withdrawing: number };
  lifetime: { conversions: number; coins: number };
  staffNames: Record<string, string>;
  conversions: {
    id: string;
    providerId: string;
    offerName: string | null;
    coins: number;
    status: string;
    ip: string | null;
    /** Where the IP is from: this site's own traffic ('site'), else the network's report. */
    country: string | null;
    countrySource: 'site' | 'network' | null;
    /** This user has used the IP on this site. */
    ipUsedHere: boolean;
    clickId: string | null;
    createdAt: number;
  }[];
  withdrawals: {
    id: string;
    coins: number;
    amountMinor: number;
    status: string;
    item: { amount: number; productName: string; productCurrency: string | null } | null;
    createdAt: number;
  }[];
  ledger: {
    id: string;
    type: string;
    memo: string | null;
    createdBy: string;
    createdAt: number;
    available: number;
    pending: number;
    withdrawing: number;
  }[];
  ips: {
    ip: string;
    country: string | null;
    asn: number | null;
    asOrg: string | null;
    hits: number;
    lastSeenAt: number;
  }[];
  sharedIps: { userId: string; ip: string; username: string }[];
  devices: { deviceId: string; platform: string | null; lastSeenAt: number }[];
  sharedDevices: { userId: string; deviceId: string; username: string }[];
  /** What the Android app reported (client-controlled). Ids only as short hash prefixes. */
  appDevices?: AppDevice[];
  /** Other accounts whose app reported the same Android ID or advertising ID. */
  sharedAppDevices?: {
    userId: string;
    username: string;
    androidId: boolean;
    advertisingId: boolean;
  }[];
  flags: {
    id: string;
    code: string;
    severity: string;
    status: string;
    raisedBy: string;
    evidence: unknown;
    createdAt: number;
    aiAction: string | null;
    aiReasoning: string | null;
    aiConfidence: number | null;
  }[];
  timeline: {
    id: string;
    type: string;
    actor: string | null;
    ip: string | null;
    country: string | null;
    occurredAt: number;
  }[];
  idCheck?: IdCheck;
  holds: { id: string; coins: number; reason: string; releaseAt: number | null }[];
}

interface AppDevice {
  id: string;
  brand: string | null;
  model: string | null;
  device: string | null;
  appVersion: string | null;
  simProvider: string | null;
  timezone: string | null;
  deviceCountry: string | null;
  deviceLanguage: string | null;
  playStoreInstall: boolean | null;
  developerMode: boolean | null;
  rooted: boolean | null;
  emulator: boolean | null;
  tablet: boolean | null;
  wifiOnly: boolean | null;
  clonedApp: boolean | null;
  secondaryUser: boolean | null;
  keyVerified: boolean;
  hasAdvertisingId: boolean;
  hasAppsflyerId: boolean;
  androidIdHash: string | null;
  installIdHash: string | null;
  advertisingIdHash: string | null;
  firstSeenAt: number;
  lastSeenAt: number;
}

const PROFILE_TABS = [
  { key: 'earnings', label: 'Earnings' },
  { key: 'cashouts', label: 'Cash-outs' },
  { key: 'identity', label: 'Identity' },
  { key: 'ledger', label: 'Ledger' },
  { key: 'network', label: 'Network' },
  { key: 'activity', label: 'Activity' },
] as const;
type ProfileTab = (typeof PROFILE_TABS)[number]['key'];

const STATUS_ACTIONS: {
  status: string;
  label: string;
  title: (u: string) => string;
  body: string;
  tone?: 'danger';
}[] = [
  {
    status: 'active',
    label: 'Set active',
    title: (u) => `Make ${u} active again?`,
    body: 'Normal access: earn and cash out.',
  },
  {
    status: 'review',
    label: 'Put in review',
    title: (u) => `Put ${u} in review?`,
    body: 'They can keep earning; cash-outs wait for staff.',
  },
  {
    status: 'restricted',
    label: 'Restrict',
    title: (u) => `Restrict ${u}?`,
    body: 'They can sign in but not cash out.',
    tone: 'danger',
  },
  {
    status: 'blocked',
    label: 'Block',
    title: (u) => `Block ${u}?`,
    body: 'Signs them out everywhere and stops all access.',
    tone: 'danger',
  },
];

const TXN_LABEL: Record<string, string> = {
  offer_credit: 'Offer credit',
  hold_release: 'Hold released',
  reversal: 'Reversal',
  withdrawal_reserve: 'Cash-out reserved',
  withdrawal_paid: 'Cash-out paid',
  withdrawal_release: 'Cash-out returned',
  referral_commission: 'Referral commission',
  promo: 'Promo',
  streak: 'Daily bonus',
  spin: 'Spin reward',
  leaderboard: 'Leaderboard prize',
  level_reward: 'Level reward',
  legacy_import: 'Old account balance',
  admin_adjustment: 'Staff credit',
};

/** Separator for the meta line; dropped on phones where the line wraps. */
const Dot = () => (
  <span aria-hidden="true" className="hidden sm:inline">
    ·
  </span>
);

function BackToUsers() {
  return (
    <Link
      to="/users"
      className="-my-2 inline-flex items-center gap-1 py-2 text-[13px] font-semibold text-stone hover:text-ink"
    >
      <ArrowLeft size={14} aria-hidden="true" /> Users
    </Link>
  );
}

/** Status changes as a small menu; closes on outside click, Escape (focus back on the button) or a pick. */
function StatusMenu({
  current,
  onPick,
}: {
  current: string;
  onPick: (a: (typeof STATUS_ACTIONS)[number]) => void;
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const onDown = (e: PointerEvent) => {
      if (d.open && !d.contains(e.target as Node)) d.open = false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && d.open) {
        d.open = false;
        d.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  return (
    <details ref={ref} className="group relative">
      <summary className="btn-ghost min-h-10 cursor-pointer list-none px-3.5 py-2 text-[14px] group-open:border-ink [&::-webkit-details-marker]:hidden">
        <UserCog size={15} aria-hidden="true" /> Status
        <ChevronDown size={14} aria-hidden="true" className="transition group-open:rotate-180" />
      </summary>
      <div className="absolute right-0 z-20 mt-2 w-[min(18rem,calc(100vw-2rem))] rounded-[14px] border-[1.5px] border-ink bg-paper p-1.5 shadow-[3px_3px_0_#111110]">
        <div className="px-3 pb-1.5 pt-1 text-[12px] font-semibold text-stone">
          Now: {statusLabel(current)}
        </div>
        {STATUS_ACTIONS.filter((a) => a.status !== current).map((a) => (
          <button
            key={a.status}
            type="button"
            onClick={() => {
              const d = ref.current!;
              d.open = false;
              // The dialog's busy state and focus return land on the visible menu button.
              d.querySelector('summary')?.focus();
              onPick(a);
            }}
            className={`block w-full rounded-[10px] px-3 py-2 text-left transition hover:bg-canvas ${a.tone === 'danger' ? 'text-berry-deep' : ''}`}
          >
            <div className="text-[14px] font-bold">{a.label}</div>
            <div className="text-[12px] leading-snug text-stone">{a.body}</div>
          </button>
        ))}
      </div>
    </details>
  );
}

export function UserDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const can = useCan();
  const [tab, setTab] = useState<ProfileTab>('earnings');
  const { data, error, refetch } = useQuery({
    queryKey: ['user', id],
    queryFn: () => api<UserDetailData>(`/users/${id}`),
  });
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['user', id] }),
      qc.invalidateQueries({ queryKey: ['users'] }),
      qc.invalidateQueries({ queryKey: ['overview'] }),
    ]);
  if (error && !data)
    return (
      <div className="space-y-5">
        <BackToUsers />
        <ErrorNote error={error} onRetry={() => void refetch()} />
      </div>
    );
  if (!data)
    return (
      <div className="space-y-5" role="status" aria-label="Loading">
        <div className="h-5 w-16 animate-pulse rounded bg-ink/[0.07]" />
        <div className="card h-56 animate-pulse" />
        <div className="h-10 w-80 max-w-full animate-pulse rounded-[12px] bg-ink/[0.06]" />
        <div className="card h-64 animate-pulse" />
      </div>
    );
  const u = data.user;
  const deleted = u.status === 'deleted';
  const names = data.staffNames;
  const country =
    u.signupCountry && u.countryCode && u.signupCountry !== u.countryCode
      ? `Signed up in ${u.signupCountry}, now ${u.countryCode}`
      : u.signupCountry || u.countryCode
        ? `From ${u.signupCountry ?? u.countryCode}`
        : 'No country';

  const setStatus = async (a: (typeof STATUS_ACTIONS)[number]) => {
    const reason = await confirmAction({
      title: a.title(u.username),
      body: a.body,
      confirmLabel: a.label,
      ...(a.tone ? { tone: a.tone } : {}),
      reason: { label: 'Reason (kept in the audit log)' },
    });
    if (reason === null) return;
    await run(
      () => api(`/users/${id}/status`, { body: { status: a.status, reason } }),
      `${u.username} is now ${statusLabel(a.status).toLowerCase()}.`,
    );
    await refresh();
  };
  const credit = async () => {
    const r = await askAmount({
      title: `Credit coins to ${u.username}`,
      body: 'Adds coins to their available balance as a staff credit. To take coins back, reverse the original credit instead.',
      confirmLabel: 'Credit coins',
      tone: 'coin',
      amount: {
        label: 'Coins',
        min: 1,
        max: 1_000_000,
        suffix: 'coins',
        hint: (n) => `${fmtNum(n)} coins = ${fmtUsd(n / 1000)}`,
      },
      reason: {
        label: 'Reason (users see it in their wallet history)',
        presets: ['Missing offer credit', 'Support goodwill', 'Contest prize'],
      },
    });
    if (!r) return;
    await run(
      () =>
        api(`/users/${id}/adjust`, {
          body: { coins: r.amount, reason: r.reason, requestId: crypto.randomUUID() },
        }),
      `Credited ${fmtNum(r.amount)} coins.`,
    );
    await refresh();
  };
  const resolveFlag = async (flagId: string, status: 'confirmed' | 'dismissed') => {
    const note = await confirmAction({
      title: status === 'confirmed' ? 'Confirm this flag?' : 'Dismiss this flag?',
      body:
        status === 'confirmed'
          ? 'Marks it as real abuse. It stays on the account and counts against future cash-outs.'
          : 'Marks it as a false alarm and removes it from the risk score.',
      confirmLabel: status === 'confirmed' ? 'Confirm flag' : 'Dismiss flag',
      ...(status === 'confirmed' ? { tone: 'danger' as const } : {}),
      reason: { label: 'Note (optional, at least a few words)', placeholder: 'What you checked' },
    });
    if (note === null) return;
    await run(() => api(`/flags/${flagId}/status`, { body: { status, note } }), `Flag ${status}.`);
    await refresh();
  };
  const stopAdMeasurement = async () => {
    const reason = await confirmAction({
      title: `Stop ad measurement for ${u.username}?`,
      body: 'No more app events about this account go to AppsFlyer. This can’t be undone here.',
      confirmLabel: 'Stop ad measurement',
      reason: { label: 'Reason (kept in the audit log)', placeholder: 'e.g. asked by email' },
    });
    if (reason === null) return;
    await run(
      () => api(`/users/${id}/attribution-opt-out`, { body: { reason } }),
      'Ad measurement stopped.',
    );
    await refresh();
  };
  const openFlags = data.flags.filter((f) => f.status === 'open');
  const sharedApp = data.sharedAppDevices ?? [];
  const appDevices = data.appDevices ?? [];
  const shared = data.sharedIps.length + data.sharedDevices.length + sharedApp.length;

  return (
    <div className="space-y-5">
      <BackToUsers />

      {/* Header */}
      <section className="card p-4 sm:p-5">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-4">
          <div className="flex min-w-0 flex-[1_1_20rem] items-start gap-3.5 sm:gap-4">
            <span className="sm:hidden">
              {deleted ? <DeletedMark size={44} /> : <Initial name={u.username} size={44} />}
            </span>
            <span className="hidden sm:block">
              {deleted ? <DeletedMark size={56} /> : <Initial name={u.username} size={56} />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                <h1 className="min-w-0 text-[24px] font-extrabold leading-tight tracking-[-0.025em] [overflow-wrap:anywhere] sm:text-[26px]">
                  {deleted ? 'Deleted account' : u.username}
                </h1>
                <span className="flex flex-wrap gap-1.5">
                  <StatusPill status={u.status} />
                  {!deleted && (
                    <VerificationPills
                      emailVerifiedAt={u.emailVerifiedAt}
                      idCheck={data.idCheck}
                      onIdClick={() => setTab('identity')}
                    />
                  )}
                </span>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-stone">
                {u.email && (
                  <>
                    <span className="min-w-0 break-all">{u.email}</span>
                    <Dot />
                  </>
                )}
                <Mono>{u.id}</Mono>
                <Dot />
                <span className="whitespace-nowrap">
                  Joined <TimeAgo t={u.createdAt} />
                </span>
                <Dot />
                <span>{country}</span>
              </div>
              {deleted ? (
                <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-graphite">
                  The owner deleted this account
                  {u.deletedAt ? (
                    <>
                      {' '}
                      <TimeAgo t={u.deletedAt} />
                    </>
                  ) : (
                    ' (erasure still finishing)'
                  )}
                  . Email, name, password and their messages are erased and what was left of the
                  balance was forfeited. Earnings, ledger and cash-out records stay, under this id
                  only. It can’t be restored.
                </p>
              ) : (
                u.statusReason &&
                u.status !== 'active' && (
                  <p className="mt-2 text-[13px] text-graphite">Status note: {u.statusReason}</p>
                )
              )}
            </div>
          </div>
          {!deleted && (can('users.adjust') || can('users.manage')) && (
            <div className="flex gap-2 max-sm:w-full max-sm:[&>*]:flex-1 max-sm:[&_summary]:w-full">
              {can('users.adjust') && (
                <button className="btn-ghost min-h-10 px-3.5 py-2 text-[14px]" onClick={credit}>
                  <Coins size={15} aria-hidden="true" /> Credit coins
                </button>
              )}
              {can('users.manage') && (
                <StatusMenu current={u.status} onPick={(a) => void setStatus(a)} />
              )}
            </div>
          )}
        </div>
        <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-4 border-t border-hairline pt-4 sm:grid-cols-3 lg:grid-cols-5">
          <Stat
            label="Available"
            value={fmtNum(data.balance.available)}
            unit="coins"
            sub={fmtUsd(data.balance.available / 1000)}
            accent
            className="max-sm:col-span-2"
          />
          <Stat
            label="Pending"
            value={fmtNum(data.balance.pending)}
            unit="coins"
            sub={
              data.holds.length
                ? `${data.holds.length} hold${data.holds.length === 1 ? '' : 's'}`
                : 'No holds'
            }
          />
          <Stat
            label="Cashing out"
            value={fmtNum(data.balance.withdrawing)}
            unit="coins"
            sub={fmtUsd(data.balance.withdrawing / 1000)}
          />
          <Stat
            label="Earned, lifetime"
            value={fmtNum(data.lifetime.coins)}
            unit="coins"
            sub={`${fmtNum(data.lifetime.conversions)} offer${data.lifetime.conversions === 1 ? '' : 's'}`}
          />
          <Stat
            label="Risk score"
            value={String(u.riskScore)}
            unit="/ 100"
            sub={`${openFlags.length} open flag${openFlags.length === 1 ? '' : 's'}`}
            tone={u.riskScore >= 80 ? 'text-berry-deep' : ''}
          />
        </dl>
      </section>

      {/* Flags first: they're what needs a decision. */}
      {data.flags.length > 0 && (
        <Card
          pad={false}
          title={
            <span className="inline-flex items-center gap-2">
              Risk flags
              {openFlags.length > 0 && <Pill tone="berry">{openFlags.length} open</Pill>}
            </span>
          }
        >
          <ul className="divide-y divide-hairline">
            {data.flags.map((f) => (
              <li key={f.id} className="px-4 py-4 sm:px-5">
                <div className="flex flex-wrap items-start gap-x-3 gap-y-3">
                  <div className="flex min-w-0 flex-[1_1_16rem] items-start gap-2.5">
                    <Flag
                      size={15}
                      aria-hidden="true"
                      className={`mt-[3px] shrink-0 ${f.status === 'open' ? 'text-berry-deep' : 'text-stone'}`}
                    />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-bold">{flagLabel(f.code)}</span>
                        <SeverityPill severity={f.severity} />
                        <StatusPill status={f.status} />
                      </div>
                      <div className="mt-0.5 text-[12px] text-stone">
                        Raised by {actorLabel(f.raisedBy, names)} · <TimeAgo t={f.createdAt} />
                      </div>
                    </div>
                  </div>
                  {f.status === 'open' && can('users.manage') && (
                    <span className="flex gap-2 max-sm:w-full max-sm:pl-[25px] max-sm:[&>*]:flex-1">
                      <button
                        className="btn-ghost min-h-9 px-3.5 py-1.5 text-[13px]"
                        onClick={() => resolveFlag(f.id, 'dismissed')}
                      >
                        Dismiss
                      </button>
                      <button
                        className="btn-ghost min-h-9 px-3.5 py-1.5 text-[13px] text-berry-deep hover:border-berry-deep"
                        onClick={() => resolveFlag(f.id, 'confirmed')}
                      >
                        Confirm
                      </button>
                    </span>
                  )}
                  {/* A decision can be reversed: a confirmed flag found to be a false alarm, or the
                      other way round. */}
                  {f.status !== 'open' && can('users.manage') && (
                    <span className="flex gap-2 max-sm:w-full max-sm:pl-[25px] max-sm:[&>*]:flex-1">
                      <button
                        className="btn-ghost min-h-9 px-3.5 py-1.5 text-[13px]"
                        onClick={() =>
                          resolveFlag(f.id, f.status === 'confirmed' ? 'dismissed' : 'confirmed')
                        }
                      >
                        {f.status === 'confirmed' ? 'Dismiss instead' : 'Confirm instead'}
                      </button>
                    </span>
                  )}
                </div>
                <div className="sm:pl-[25px]">
                  {f.aiAction && (
                    <div className="mt-3 rounded-[10px] bg-canvas px-3 py-2.5 text-[13px]">
                      <span className="font-bold">
                        Claude suggests:{' '}
                        {humanize(f.aiAction.replace(/^recommend_/, '')).toLowerCase()}
                      </span>
                      {f.aiConfidence !== null && (
                        <span className="tabular-nums text-stone">
                          {' '}
                          · {Math.round(f.aiConfidence * 100)}% sure
                        </span>
                      )}
                      {f.aiReasoning && (
                        <p className="mt-1 leading-relaxed text-graphite">{f.aiReasoning}</p>
                      )}
                    </div>
                  )}
                  <FlagEvidence evidence={f.evidence} />
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Tabs
        label="Profile sections"
        value={tab}
        onChange={setTab}
        items={PROFILE_TABS.map((t) => ({
          key: t.key,
          label: t.label,
          count:
            t.key === 'earnings'
              ? data.conversions.length
              : t.key === 'cashouts'
                ? data.withdrawals.length
                : t.key === 'network'
                  ? shared || undefined
                  : t.key === 'identity'
                    ? data.idCheck?.verifications.length || undefined
                    : undefined,
          hot: t.key === 'network' && data.sharedDevices.length + sharedApp.length > 0,
        }))}
      />

      {tab === 'earnings' && (
        <div className="card overflow-hidden">
          {data.conversions.length === 0 ? (
            <EmptyState icon={Coins} title="No offers completed yet" />
          ) : (
            <ScrollX>
              <table className="data-table table-compact table-baseline min-w-[680px]">
                <thead>
                  <tr>
                    <th>Offer</th>
                    <th>Network</th>
                    <th className="text-right">Coins</th>
                    <th>Status</th>
                    <th>From</th>
                    <th className="text-right">When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.conversions.map((c) => (
                    <tr key={c.id}>
                      <td
                        className="max-w-[280px] truncate font-semibold"
                        title={c.offerName ?? ''}
                      >
                        {c.offerName ?? 'Offer'}
                      </td>
                      <td className="text-[13px]">{c.providerId}</td>
                      <td className="text-right">
                        <CoinAmount n={c.coins} />
                      </td>
                      <td>
                        <span className="inline-flex items-center gap-1.5">
                          <StatusPill status={c.status} />
                          {!c.clickId && <Pill tone="sun">No click</Pill>}
                        </span>
                      </td>
                      <td
                        className="whitespace-nowrap text-[13px] text-stone"
                        title={
                          !c.ip
                            ? undefined
                            : c.countrySource === 'site'
                              ? `The network reported this IP. ${c.country} is where it connected from when used on this site${c.ipUsedHere ? ' by this user' : ', by another account'}.`
                              : c.countrySource === 'network'
                                ? 'The network reported this IP and country. This IP was never used on this site.'
                                : 'The network reported this IP. It was never used on this site, so its country is unknown.'
                        }
                      >
                        {c.ip ? <code className="text-[12px]">{c.ip}</code> : '—'} {c.country}
                        {c.ip && !c.ipUsedHere && (
                          <span className="ml-1 text-[12px]">· not seen on site</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap text-right text-[13px] text-stone">
                        <TimeAgo t={c.createdAt} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>
      )}

      {tab === 'cashouts' && (
        <div className="card overflow-hidden">
          {data.withdrawals.length === 0 ? (
            <EmptyState title="No cash-outs yet" />
          ) : (
            <ScrollX>
              <table className="data-table table-compact table-baseline min-w-[520px]">
                <thead>
                  <tr>
                    <th>Card</th>
                    <th className="text-right">Coins</th>
                    <th>Status</th>
                    <th className="text-right">When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.withdrawals.map((w) => (
                    <tr key={w.id}>
                      <td>
                        <Link
                          to={`/withdrawals?tab=all&id=${w.id}`}
                          className="font-semibold underline decoration-coin decoration-2 underline-offset-4"
                        >
                          {w.item
                            ? `${fmtMoney(w.item.amount, w.item.productCurrency ?? 'USD')} ${w.item.productName}`
                            : `${fmtUsd(w.amountMinor / 100)} gift card`}
                        </Link>
                      </td>
                      <td className="text-right">
                        <CoinAmount n={w.coins} />
                      </td>
                      <td>
                        <StatusPill status={w.status} />
                      </td>
                      <td className="whitespace-nowrap text-right text-[13px] text-stone">
                        <TimeAgo t={w.createdAt} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>
      )}

      {tab === 'identity' &&
        (data.idCheck ? (
          <IdChecks userId={u.id} check={data.idCheck} names={names} deleted={deleted} />
        ) : (
          <div className="card">
            <EmptyState title="Reload to see identity checks" />
          </div>
        ))}

      {tab === 'ledger' && (
        <div className="card overflow-hidden">
          {data.ledger.length === 0 ? (
            <EmptyState title="No ledger entries" />
          ) : (
            <ScrollX>
              <table className="data-table table-compact min-w-[720px]">
                <thead>
                  <tr>
                    <th>What</th>
                    <th className="text-right">Available</th>
                    <th className="text-right">Pending</th>
                    <th className="text-right">Cashing out</th>
                    <th>By</th>
                    <th className="text-right">When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ledger.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <div className="font-semibold">{TXN_LABEL[t.type] ?? humanize(t.type)}</div>
                        {t.memo && (
                          <div
                            className="max-w-[280px] truncate text-[12px] text-stone"
                            title={t.memo}
                          >
                            {t.memo}
                          </div>
                        )}
                      </td>
                      <Delta n={t.available} />
                      <Delta n={t.pending} />
                      <Delta n={t.withdrawing} />
                      <td className="text-[13px]">{actorLabel(t.createdBy, names)}</td>
                      <td className="whitespace-nowrap text-right text-[13px] text-stone">
                        <TimeAgo t={t.createdAt} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>
      )}

      {tab === 'network' && (
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <Card
            title="Other accounts on the same network"
            className={shared ? '!border-berry/40' : ''}
          >
            {shared === 0 ? (
              <p className="flex items-center gap-2 text-[14px] text-mint-deep">
                <Check size={16} aria-hidden="true" /> No other account shares an IP, browser or
                phone with this one.
              </p>
            ) : (
              <ul className="space-y-3">
                {sharedApp.map((s) => (
                  <li
                    key={`a${s.userId}`}
                    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5"
                  >
                    <UserChip id={s.userId} username={s.username} />
                    <Pill tone="berry">
                      <Smartphone size={12} aria-hidden="true" /> Same phone (
                      {[s.androidId && 'Android ID', s.advertisingId && 'ad ID']
                        .filter(Boolean)
                        .join(', ')}
                      )
                    </Pill>
                  </li>
                ))}
                {data.sharedDevices.map((s) => (
                  <li
                    key={`d${s.userId}${s.deviceId}`}
                    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5"
                  >
                    <UserChip id={s.userId} username={s.username} />
                    <Pill tone="berry">
                      <Monitor size={12} aria-hidden="true" /> Same browser
                    </Pill>
                  </li>
                ))}
                {data.sharedIps.map((s) => (
                  <li
                    key={`i${s.userId}${s.ip}`}
                    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5"
                  >
                    <UserChip id={s.userId} username={s.username} />
                    <Pill tone="coin">
                      <Network size={12} aria-hidden="true" /> Same IP {s.ip}
                    </Pill>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card pad={false} title="Where they sign in from">
            <div className="px-4 pb-1 pt-3 text-[12px] font-semibold text-stone sm:px-5">
              IP addresses ({data.ips.length})
            </div>
            {data.ips.length === 0 ? (
              <p className="px-4 pb-3 text-[13px] text-stone sm:px-5">None recorded.</p>
            ) : (
              <ul className="divide-y divide-hairline px-4 text-[13px] sm:px-5">
                {data.ips.map((i) => (
                  <li key={i.ip} className="flex items-baseline gap-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <code className="font-semibold text-ink">{i.ip}</code>
                      <span className="ml-2 text-graphite">{i.country ?? '—'}</span>
                      <div className="truncate text-[12px] text-stone" title={i.asOrg ?? ''}>
                        {[i.asn ? `AS${i.asn}` : null, i.asOrg].filter(Boolean).join(' ') ||
                          'Network unknown'}
                      </div>
                    </div>
                    <span className="shrink-0 whitespace-nowrap text-right text-[12px] tabular-nums text-stone">
                      {fmtNum(i.hits)}× · <TimeAgo t={i.lastSeenAt} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-1 border-t border-hairline px-4 pb-1 pt-3 text-[12px] font-semibold text-stone sm:px-5">
              Browsers ({data.devices.length})
            </div>
            {data.devices.length === 0 ? (
              <p className="px-4 pb-3 text-[13px] text-stone sm:px-5">None recorded.</p>
            ) : (
              <ul className="divide-y divide-hairline px-4 pb-1 text-[13px] sm:px-5">
                {data.devices.map((d) => (
                  <li key={d.deviceId} className="flex items-center gap-3 py-2.5">
                    <Monitor size={14} aria-hidden="true" className="shrink-0 text-stone" />
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-semibold">{d.platform ?? 'Unknown platform'}</span>{' '}
                      <code className="text-[12px] text-stone">{d.deviceId}</code>
                    </span>
                    <span className="shrink-0 text-[12px] text-stone">
                      <TimeAgo t={d.lastSeenAt} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          {appDevices.length > 0 && (
            <Card pad={false} title="Android app" className="lg:col-span-2">
              <p className="px-4 pt-3 text-[12px] text-stone sm:px-5">
                What the app reported about the phone. The phone can fake any of it, so it only ever
                adds suspicion. Ids are keyed hashes (first 12 characters shown); the advertising ID
                itself stays encrypted.
              </p>
              <ul className="divide-y divide-hairline px-4 text-[13px] sm:px-5">
                {appDevices.map((d) => (
                  <AppDeviceRow key={d.id} d={d} />
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-3 border-t border-hairline px-4 py-3 text-[13px] sm:px-5">
                <span className="text-graphite">
                  Ad measurement (AppsFlyer):{' '}
                  {u.attributionOptOutAt ? (
                    <span className="font-semibold">
                      stopped <TimeAgo t={u.attributionOptOutAt} />
                    </span>
                  ) : (
                    <span className="font-semibold">on</span>
                  )}
                </span>
                {!u.attributionOptOutAt && !deleted && can('users.manage') && (
                  <button
                    type="button"
                    className="btn-ghost ml-auto h-9 px-3 py-0 text-[13px]"
                    onClick={() => void stopAdMeasurement()}
                  >
                    Stop ad measurement
                  </button>
                )}
              </div>
            </Card>
          )}
        </div>
      )}

      {tab === 'activity' && (
        <div className="card overflow-hidden">
          {data.timeline.length === 0 ? (
            <EmptyState title="No activity recorded" />
          ) : (
            <ul className="divide-y divide-hairline">
              {data.timeline.map((e) => (
                <li key={e.id} className="flex items-baseline gap-3 px-4 py-3 text-[14px] sm:px-5">
                  <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="font-semibold">{humanize(e.type)}</span>
                    {e.actor && e.actor !== u.id && (
                      <span className="text-[13px] text-stone">
                        by {actorLabel(e.actor, names)}
                      </span>
                    )}
                    {(e.ip || e.country) && (
                      <span className="text-[13px] text-stone">
                        {e.ip && <code className="text-[12px]">{e.ip}</code>} {e.country}
                      </span>
                    )}
                  </div>
                  <span className="shrink-0 text-[13px] text-stone">
                    <TimeAgo t={e.occurredAt} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Card title="Account details">
        <KV
          items={[
            ['Referral code', <Mono key="r">{u.referralCode}</Mono>],
            [
              'Referred by',
              u.referredBy ? (
                <Link
                  key="b"
                  to={`/users/${u.referredBy}`}
                  className="underline decoration-coin decoration-2 underline-offset-4"
                >
                  {u.referredBy}
                </Link>
              ) : (
                'Nobody'
              ),
            ],
          ]}
        />
      </Card>
    </div>
  );
}

/** One app install: the phone, the checks that matter for fraud, and the hashed ids. */
function AppDeviceRow({ d }: { d: AppDevice }) {
  const checks: [string, boolean | null, boolean][] = [
    // label, value, is "yes" bad?
    ['Emulator', d.emulator, true],
    ['Cloned app', d.clonedApp, true],
    ['Rooted', d.rooted, true],
    ['Secondary user', d.secondaryUser, true],
    ['Developer mode', d.developerMode, true],
    ['From Play Store', d.playStoreInstall, false],
    ['Tablet', d.tablet, false],
    ['Wi-Fi only', d.wifiOnly, false],
  ];
  const known = checks.filter(([, v]) => v !== null);
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Smartphone size={14} aria-hidden="true" className="shrink-0 self-center text-stone" />
        <span className="font-semibold">
          {[d.brand, d.model].filter(Boolean).join(' ') || 'Unknown phone'}
        </span>
        {d.appVersion && <span className="text-stone">app {d.appVersion}</span>}
        {!d.keyVerified && <Pill tone="sun">Unverified launch</Pill>}
        <span className="ml-auto shrink-0 text-[12px] text-stone">
          first <TimeAgo t={d.firstSeenAt} /> · last <TimeAgo t={d.lastSeenAt} />
        </span>
      </div>
      <div className="mt-1 text-[12px] text-graphite">
        {[
          d.deviceCountry && `Country ${d.deviceCountry}`,
          d.deviceLanguage && `language ${d.deviceLanguage}`,
          d.timezone,
          d.simProvider && `SIM ${d.simProvider}`,
          d.hasAdvertisingId ? 'advertising ID on file' : 'no advertising ID',
          d.hasAppsflyerId && 'AppsFlyer id on file',
        ]
          .filter(Boolean)
          .join(' · ')}
      </div>
      {known.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {known.map(([label, v, yesIsBad]) => (
            <Pill key={label} tone={v === yesIsBad ? 'berry' : 'neutral'}>
              {label}: {v ? 'yes' : 'no'}
            </Pill>
          ))}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-stone">
        {(
          [
            ['Android ID', d.androidIdHash],
            ['Install id', d.installIdHash],
            ['Advertising ID', d.advertisingIdHash],
          ] as const
        )
          .filter(([, h]) => h)
          .map(([label, h]) => (
            <span key={label}>
              {label} <code className="text-ink">{h}…</code>
            </span>
          ))}
      </div>
    </li>
  );
}

function Stat({
  label,
  value,
  unit,
  sub,
  accent,
  tone = '',
  className = '',
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  accent?: boolean;
  tone?: string;
  className?: string;
}) {
  return (
    <div className={`min-w-0 ${className}`}>
      <dt className="text-[12px] font-semibold text-stone">{label}</dt>
      <dd
        className={`mt-1 text-[22px] font-extrabold leading-tight tracking-[-0.02em] tabular-nums ${accent ? 'text-coin-deep' : tone}`}
      >
        {value}
        {unit && (
          <span className="ml-1 text-[12px] font-semibold tracking-normal text-stone">{unit}</span>
        )}
      </dd>
      {sub && <dd className="mt-0.5 text-[12px] text-stone">{sub}</dd>}
    </div>
  );
}

function Delta({ n }: { n: number }) {
  return (
    <td
      className={`whitespace-nowrap text-right font-bold tabular-nums ${n > 0 ? 'text-mint-deep' : n < 0 ? 'text-berry-deep' : 'text-ink/25'}`}
    >
      {n === 0 ? (
        <span aria-label="No change">·</span>
      ) : (
        `${n > 0 ? '+' : '−'}${fmtNum(Math.abs(n))}`
      )}
    </td>
  );
}

function FlagEvidence({ evidence }: { evidence: unknown }) {
  if (!evidence || typeof evidence !== 'object') return null;
  const entries = Object.entries(evidence as Record<string, unknown>).slice(0, 10);
  if (!entries.length) return null;
  const label = (k: string) => {
    const t = k
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .toLowerCase();
    return (t.charAt(0).toUpperCase() + t.slice(1)).replace(
      /\b(ip|asn|id)(s?)\b/gi,
      (_, w, s) => `${w.toUpperCase()}${s}`,
    );
  };
  const value = (v: unknown): ReactNode => {
    if (typeof v === 'string' && /^usr_[0-9A-Za-z]+$/.test(v))
      return (
        <Link
          to={`/users/${v}`}
          className="underline decoration-coin decoration-2 underline-offset-2 hover:text-ink"
        >
          {v}
        </Link>
      );
    if (typeof v === 'number') return fmtNum(v);
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (v === null || v === undefined) return '—';
    if (Array.isArray(v))
      return v.length === 0
        ? 'None'
        : v.slice(0, 12).map((x, i) => (
            <span key={i}>
              {i > 0 && ', '}
              {value(x)}
            </span>
          ));
    if (typeof v === 'object')
      return Object.entries(v as Record<string, unknown>)
        .slice(0, 6)
        .map(([k, x], i) => (
          <span key={k}>
            {i > 0 && ' · '}
            <span className="font-normal text-stone">{label(k)}</span> {value(x)}
          </span>
        ));
    return String(v);
  };
  return (
    <dl className="mt-3 grid grid-cols-[minmax(80px,auto)_1fr] gap-x-4 gap-y-1 text-[12px]">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-stone">{label(k)}</dt>
          <dd className="break-words font-semibold">{value(v)}</dd>
        </div>
      ))}
    </dl>
  );
}
